'use client';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ReportMessageSource } from '~context/report/index';
import { ReportActionsContext } from '~context/report/index';
import type { TMessage } from '~entities/messages';
import type {
	ReportCharacterParams,
	ReportMediaParams,
	ReportMessageParams,
} from '~features/report';
import {
	ReportCharacterModal,
	ReportMediaModal,
	ReportMessageModal,
} from '~features/report';
import { useModal } from '~hooks/useModal';
import type { GeneratedMedia } from '~services/api/generation';

export const ReportActionsProvider: FC<PropsWithChildren> = ({ children }) => {
	const {
		isOpen: reportMessageIsOpen,
		open: reportMessageOpen,
		close: reportMessageClose,
	} = useModal();
	const {
		isOpen: reportCharacterIsOpen,
		open: reportCharacterOpen,
		close: reportCharacterClose,
	} = useModal();
	const {
		isOpen: reportMediaIsOpen,
		open: reportMediaOpen,
		close: reportMediaClose,
	} = useModal();

	const [message, setMessage] = useState<TMessage | undefined>();
	const [source, setSource] = useState<ReportMessageSource | undefined>();
	const [botName, setBotName] = useState<string | undefined>();
	const [botId, setBotId] = useState<number | undefined>();
	const [mediaId, setMediaId] = useState<GeneratedMedia['id'] | undefined>();
	const callback = useRef(() => {
		//
	});

	const reportMessageClosed = useCallback(() => {
		reportMessageClose(false);
	}, [reportMessageClose]);

	const reportCharacterClosed = useCallback(() => {
		reportCharacterClose(false);
	}, [reportCharacterClose]);

	const reportMediaClosed = useCallback(() => {
		reportMediaClose(false);
	}, [reportMediaClose]);

	const reportMessage = useCallback(
		({ botName, message, source, onReported }: ReportMessageParams) =>
			() => {
				setSource(source);
				setMessage(message);
				setBotName(botName);
				reportMessageOpen()();
				callback.current = onReported || callback.current;
			},
		[reportMessageOpen]
	);

	const reportCharacter = useCallback(
		({ botName, botId, onReported }: ReportCharacterParams) =>
			() => {
				setBotName(botName);
				setBotId(botId);
				reportCharacterOpen()();
				callback.current = onReported || callback.current;
			},
		[reportCharacterOpen]
	);

	const reportMedia = useCallback(
		({ mediaId, onReported }: ReportMediaParams) =>
			() => {
				setMediaId(mediaId);
				reportMediaOpen()();
				callback.current = onReported || callback.current;
			},
		[reportMediaOpen]
	);

	const value = useMemo(
		() => ({
			reportMessage,
			reportCharacter,
			reportMedia,
		}),
		[reportMessage, reportCharacter, reportMedia]
	);
	return (
		<ReportActionsContext.Provider value={value}>
			{children}
			<ReportMessageModal
				botName={botName}
				open={!!message && !!source && reportMessageIsOpen}
				source={source}
				message={message}
				locked={false}
				onReported={callback.current}
				onClose={reportMessageClosed}
			/>
			<ReportCharacterModal
				botId={botId as number}
				open={reportCharacterIsOpen}
				locked={false}
				onReported={callback.current}
				onClose={reportCharacterClosed}
			/>
			<ReportMediaModal
				mediaId={mediaId as GeneratedMedia['id']}
				open={reportMediaIsOpen}
				locked={false}
				onReported={callback.current}
				onClose={reportMediaClosed}
			/>
		</ReportActionsContext.Provider>
	);
};
